import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { diamondProducts } from "../data/mockData";

const FadeIn = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 35 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const DiamondPage = () => {
  const [activeShape, setActiveShape] = useState("All");
  const allShapes = ["All", ...new Set(diamondProducts.map(d => d.shape))];
  const filtered = activeShape === "All" ? diamondProducts : diamondProducts.filter(d => d.shape === activeShape);

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[350px] flex items-center justify-center bg-[#1a1a1a]">
        <div className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1763256614634-7feb3ff79ff3?w=1920)` }} />
        <div className="absolute inset-0 bg-black/50" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }} className="relative z-10 text-center px-6">
          <h1 className="text-4xl md:text-5xl lg:text-6xl text-white font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Loose Diamonds
          </h1>
          <p className="mt-4 text-white/60 text-sm tracking-[0.1em]"
            style={{ fontFamily: "'Inter', sans-serif" }}>
            Round Brilliant • Fancy Shapes • 0.03 - 5.00 ct
          </p>
          <div className="h-[1px] w-16 bg-[#c8a96e] mx-auto mt-6" />
        </motion.div>
      </section>

      {/* Filter */}
      <section className="py-12 bg-[#faf9f7] border-b border-[#eee]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap justify-center gap-3">
            {allShapes.map((shape) => (
              <button key={shape}
                onClick={() => setActiveShape(shape)}
                className={`px-5 py-2 text-[11px] tracking-[0.15em] uppercase transition-all duration-300 border ${
                  activeShape === shape
                    ? "bg-[#1a1a1a] text-white border-[#1a1a1a]"
                    : "bg-transparent text-[#666] border-[#ddd] hover:border-[#c8a96e] hover:text-[#c8a96e]"
                }`}
                style={{ fontFamily: "'Inter', sans-serif" }}>
                {shape}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 bg-[#faf9f7]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((diamond, i) => (
              <FadeIn key={diamond.id} delay={i * 0.05}>
                <div className="group cursor-pointer">
                  <div className="relative overflow-hidden bg-white aspect-square">
                    <img src={diamond.image} alt={diamond.shape}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-500" />
                  </div>
                  <div className="mt-4">
                    <h3 className="text-lg text-[#1a1a1a] group-hover:text-[#c8a96e] transition-colors"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {diamond.shape}
                    </h3>
                    <p className="mt-1 text-[11px] tracking-[0.1em] uppercase text-[#999]"
                      style={{ fontFamily: "'Inter', sans-serif" }}>
                      {diamond.caratRange}
                    </p>
                    <p className="mt-1 text-[11px] tracking-[0.08em] text-[#aaa]"
                      style={{ fontFamily: "'Inter', sans-serif" }}>
                      {diamond.quality}
                    </p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1a1a1a] text-center">
        <FadeIn>
          <h2 className="text-3xl md:text-4xl text-white font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Need Specific Diamonds?
          </h2>
          <p className="mt-4 text-white/50 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
            Share your requirements and we'll source the perfect stones for you.
          </p>
          <Link to="/contact-us"
            className="inline-flex items-center gap-2 mt-8 px-8 py-3 border border-[#c8a96e] text-[#c8a96e] text-[12px] tracking-[0.15em] uppercase hover:bg-[#c8a96e] hover:text-[#1a1a1a] transition-all duration-500"
            style={{ fontFamily: "'Inter', sans-serif" }}>
            Request a Quote <ArrowRight className="w-4 h-4" />
          </Link>
        </FadeIn>
      </section>
    </div>
  );
};

export default DiamondPage;
